/**
 * forecastByRep.ts — Forecast programme F5 (register row 73): the By rep
 * table's MODEL, pure, from ONE /v1/sales/forecast response. ForecastByRep.tsx
 * renders exactly what this returns.
 *
 * Spec: docs/design/calm-minimal/specs/klaxiora-forecast-spec.md §3.7, under
 * docs/design/stier/ADOPTION.md §18.7 (targets are the QUOTA GRAIN — the
 * existing PUT /v1/sales/quotas; the team figure is Σ rep targets,
 * `totalTargetPence`; won against a target is ATTRIBUTED won, as F2 reads it).
 *
 * - TARGET CELL. With a target, "{pct}% of {target}" where pct is the wire's
 *   `attainmentPct`, RENDERED not recomputed (one decimal, attainmentCell's
 *   rule), with a mini bar coloured by PACE. A real £0 target has no
 *   attainment (R3) and reads "£0.00" with no bar. No target reads U-5's
 *   "No target set" — the "Set target" control sits BESIDE those words and
 *   never replaces them.
 * - OVER TARGET (register row 90, RULED 2026-10-04): the bar is full from 100
 *   up, so above it the cell says "Over target" (forecastHero.OVER_TARGET) and
 *   the bar carries a TICK at 100 ÷ pct of its width — where the target sits
 *   inside the full fill. Keyed on the PRINTED number: 100.04 prints "100.0%"
 *   and carries no cue. The percentage itself is never touched.
 * - PACE (spec §3.7): won ÷ target against the share of the period elapsed,
 *   on the SERVER'S clock (`today`, forecastHero.periodClock). On or above
 *   pace = on; up to PACE_GAP_PTS (10) points behind = behind; more = far.
 *   The 10-point line is the hero's rule 4, so the two surfaces agree. No
 *   `today` on the wire (a pre-F1 response) = no pace and a neutral bar: the
 *   client clock is never read.
 * - SHARE OF WEIGHTED: the rep's weighted ÷ Σ weighted over the grid's rows —
 *   the LINKED population the grid names (§26 F-2a). Unlinked open value is
 *   not a rep's, and the Unattributed row reads "—" for it, as the spec says.
 *   Σ = 0 → no share ("—"), never "0%".
 * - THE UNATTRIBUTED ROW: the wire's unattributed won half, fenced count
 *   beside it, "Counts for no one", "—" in every pipeline column. With it the
 *   won column sums to the page's won (the two halves): the server builds
 *   both rows and `attributedWonPence` from the same predicate (forecast.go
 *   §5 and §2), and `wonReconciles` states whether the rows prove it.
 * - U-5 COLUMNS (RULED 2026-10-01): no Attainment column (the Target cell
 *   carries it); Overrides only while some row has one (`showOverrides`).
 * - "No wins" (spec §3.7) for a won cell with no deal behind it — counted or
 *   fenced out. A won cell with deals keeps "£X · n deals".
 */
import type { SalesForecast, SalesForecastRow } from '../../../../api/types'
import { money } from '../../../../lib/money'
import { NO_TARGET_LABEL, ABSENT, attainmentCell, fencedDealCount } from './forecastView'
import { PACE_GAP_PTS, periodClock } from './forecastHero'

export type Pace = 'on' | 'behind' | 'far'

export const NO_WINS = 'No wins'
export const COUNTS_FOR_NO_ONE = 'Counts for no one'
export const UNATTRIBUTED_LABEL = 'Unattributed'

/** Pace of a percentage against the elapsed share — null when either side is
 *  absent (no attainment, or no server clock). */
export function paceOf(pct: number | null, elapsedPct: number | null): Pace | null {
  if (pct === null || elapsedPct === null) return null
  const behindBy = elapsedPct - pct
  if (behindBy <= 0) return 'on'
  if (behindBy <= PACE_GAP_PTS) return 'behind'
  return 'far'
}

/** The server's elapsed % for the response's own period, or null pre-F1. */
export function elapsedOf(f: Pick<SalesForecast, 'periodStart' | 'periodEnd' | 'today'>): number | null {
  return f.today ? periodClock(f.periodStart, f.periodEnd, f.today).elapsedPct : null
}

export type TargetModel =
  | { kind: 'none'; text: string }
  | { kind: 'zero'; text: string }
  | { kind: 'set'; text: string; barPct: number; pace: Pace | null; over: boolean; tickPct: number | null }

export function targetModel(r: Pick<SalesForecastRow, 'targetPence' | 'attainmentPct'>, elapsedPct: number | null): TargetModel {
  if (r.targetPence === null) return { kind: 'none', text: NO_TARGET_LABEL }
  if (r.attainmentPct === null) return { kind: 'zero', text: money(r.targetPence / 100) }
  // Row 90: the cue reads what attainmentCell PRINTS (one decimal), not the raw figure.
  const over = Number(r.attainmentPct.toFixed(1)) > 100
  return {
    kind: 'set',
    text: `${attainmentCell(r.attainmentPct)} of ${money(r.targetPence / 100)}`,
    barPct: Math.max(0, Math.min(100, r.attainmentPct)),
    pace: paceOf(r.attainmentPct, elapsedPct),
    over,
    tickPct: over ? (100 / r.attainmentPct) * 100 : null,
  }
}

/** "£X · n deals", or "No wins" when no deal — counted or fenced — is behind it. */
export function wonText(pence: number, count: number, excluded: number | undefined): { won: boolean; value: string; count: string } {
  if (count + (excluded ?? 0) === 0 && pence === 0) return { won: false, value: NO_WINS, count: '' }
  return { won: true, value: money(pence / 100), count: fencedDealCount(count, excluded) }
}

/** "37%", "<1%" for a real sliver, ABSENT when the base is zero. */
export function shareText(pct: number | null): string {
  if (pct === null) return ABSENT
  if (pct > 0 && pct < 1) return '<1%'
  return `${Math.round(pct)}%`
}

export interface ByRepRow {
  row: SalesForecastRow
  target: TargetModel
  won: ReturnType<typeof wonText>
  sharePct: number | null
}

export interface ByRepModel {
  rows: ByRepRow[]
  unattributed: { won: ReturnType<typeof wonText> }
  /** Σ won column = the rows plus the Unattributed row. */
  wonTotalPence: number
  /** The page's won: the two attribution halves (the hero's Won). */
  pageWonPence: number
  /** Σ rows' won equals the wire's attributed half — the won column adds up. */
  wonReconciles: boolean
  /** U-5 columns (RULED 2026-10-01): the Overrides column renders only while
   *  at least one row carries an override; every row at 0 drops it. */
  showOverrides: boolean
  elapsedPct: number | null
}

export function byRepModel(f: SalesForecast): ByRepModel {
  const elapsedPct = elapsedOf(f)
  const weightedBase = f.rows.reduce((a, r) => a + r.weightedPence, 0)
  const rowsWon = f.rows.reduce((a, r) => a + r.wonPence, 0)
  return {
    rows: f.rows.map(r => ({
      row: r,
      target: targetModel(r, elapsedPct),
      won: wonText(r.wonPence, r.wonCount, r.wonExcludedNonGbp),
      sharePct: weightedBase > 0 ? (r.weightedPence / weightedBase) * 100 : null,
    })),
    unattributed: { won: wonText(f.unattributedWonPence, f.unattributedWonCount, f.unattributedWonExcludedNonGbp) },
    wonTotalPence: rowsWon + f.unattributedWonPence,
    pageWonPence: f.attributedWonPence + f.unattributedWonPence,
    wonReconciles: rowsWon === f.attributedWonPence,
    showOverrides: f.rows.some(r => r.overriddenCount > 0),
    elapsedPct,
  }
}
